import { FC } from 'react';
import {
  Card,
  CardContent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { IconCreditCard } from '@tabler/icons-react';
import { PRODUCTS_COLORS } from '@globalConstants';
import { t } from '@i18n';
import { parseAmount } from '@utils';
import styles from './ProductItem.styles';
import { Product } from '../../data/hooks/useCustomerLimits';

/**
 * Component that renders a single customer product with a corresponding icon and color.
 *
 * @param product - The name of the product.
 * @returns The rendered component.
 */
const ProductItem: FC<{ product: Product }> = ({ product }) => (
  <Card>
    <CardContent>
      <Stack flexDirection={'row'} alignItems={'center'} gap={2}>
        <IconCreditCard
          stroke={2}
          color={product.colors ? product.colors.main : PRODUCTS_COLORS[product.name]?.main}
        />
        <Typography variant='h3'>{product.name}</Typography>
      </Stack>

      <TableContainer sx={styles.tableContainer}>
        <Table sx={styles.table}>
          <TableHead>
            <TableRow>
              <TableCell>
                <Typography variant='subtitle2'>{t('COMMON:SUBSEGMENT')}</Typography>
              </TableCell>
              <TableCell>
                <Typography variant='subtitle2'>{t('CLIENTS:DETAIL:TRX_TYPE')}</Typography>
              </TableCell>
              <TableCell>
                <Typography variant='subtitle2'>{t('CLIENTS:DETAIL:TRX_DAILY_LIMIT')}</Typography>
              </TableCell>
              <TableCell>
                <Typography variant='subtitle2'>{t('CLIENTS:DETAIL:TRX_MONTHLY_LIMIT')}</Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {product?.subSegments?.map((row) => {
              const trxList: JSX.Element[] = [];
              const dailyLimitList: JSX.Element[] = [];
              const monthlyLimitList: JSX.Element[] = [];

              row.limits?.map((limit) => {
                trxList.push(
                  <Typography variant='caption' key={limit.transactionType}>
                    {limit.transactionType}
                  </Typography>
                );
                dailyLimitList.push(
                  <Typography variant='caption' key={limit.dailyLimit}>
                    {`${parseAmount(Number(limit.dailyLimit))}`}
                  </Typography>
                );
                monthlyLimitList.push(
                  <Typography variant='caption' key={limit.monthlyLimit}>
                    {`${parseAmount(Number(limit.monthlyLimit))}`}
                  </Typography>
                );
              });
              return (
                <TableRow key={row.name}>
                  <TableCell component='th' scope='row'>
                    <Typography
                      variant='caption'
                      alignContent={'baseline'}
                    >{`${row.name} (${row.segmentNumber})`}</Typography>
                  </TableCell>
                  <TableCell component='th' scope='row'>
                    <Stack flexDirection={'column'}>{trxList?.length > 0 && trxList}</Stack>
                  </TableCell>
                  <TableCell component='th' scope='row'>
                    <Stack flexDirection={'column'}>
                      {dailyLimitList?.length > 0 && dailyLimitList}
                    </Stack>
                  </TableCell>
                  <TableCell component='th' scope='row'>
                    <Stack flexDirection={'column'}>
                      {monthlyLimitList?.length > 0 && monthlyLimitList}
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </CardContent>
  </Card>
);

export default ProductItem;
